/**
 * @author Radek Liška <ing.radek.liska@gmail.com>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

// @ts-check

/* eslint-env browser */

import EventEmitter from 'eventemitter3';

import ViewerLifeCycle from '../life-cycle/viewer-life-cycle';

/**
 * @import { IElement } from '../../iframe'
 * @import ScheduleInfo from '../../schedule/schedule-info'
 * @import { EventTypes, ViewerController } from '../../viewer/controller'
 */

// TODO Zabezpečení:
// - Kvůli problémům s funkčností web aplikací je u sandbox povoleno "allow-same-origin".
// - To je bezpečnostním rizikem, obzvláště při spuštění aplikace ze stejného origin.
//   - Může např. zavolat `localStorage.clear()` a tím v podstatě odinstalovat lighter zařízení.
// - Ošetření do budoucna:
//   - Přidat na serveru přepínač "vypnout zabezpečení", který by toto udělal jen pro vybrané důvěryhodné weby.
//   - Přes message channel poskytovat speciální lighter rozhraní pro práci s localStorage a případné další služby.
//   - Možná zkusit zakázat URL začínající `file://` a zabránit shodě origin.
//     - Ale je otázkou jestli to nejde obejít (aliasy apod.).
/**
 * Ovladač pro zobrazování webových stránek.
 * @implements {ViewerController}
 */
export default class ViewerControllerIframe {
  /**
   * Vytvoří instanci.
   * @param {IElement} el
   * @param {number} loadDelay
   * @param {Object} [opts={}]
   * @param {boolean} [opts.scroll] Umožnit scroll v iframe.
   */
  constructor(el, loadDelay, { scroll = false } = {}) {
    this.el = el;
    /**
     * Správce událostí.
     * @private
     * @type {EventEmitter<EventTypes>}
     */
    this.emitter = new EventEmitter();
    this.loadDelay = loadDelay;
    /**
     * Timeout ID pro odpočet do maximální doby čekání na načtení iframe.
     * Dokumentace:
     * [Stack Overflow](https://stackoverflow.com/questions/375710/detect-failure-to-load-contents-of-an-iframe).
     * @private
     * @type {ReturnType<typeof setTimeout> | undefined}
     */
    this.loadTimeout = undefined;
    /** @type {ScheduleInfo | null} */
    this.scheduleInfo = null;
    this.scroll = scroll;
    this.viewerLifeCycle = new ViewerLifeCycle();
    // Navázání naslouchání na instanci.
    this.onElementError = this.onElementError.bind(this);
    this.onElementLoad = this.onElementLoad.bind(this);
    this.onLoadTimeout = this.onLoadTimeout.bind(this);
  }

  /**
   * Naváže naslouchání událostem z elementu.
   * @param {HTMLIFrameElement} el Element, kterému se naslouchá.
   * @private
   */
  bindElement(el) {
    el.addEventListener('error', this.onElementError);
    el.addEventListener('load', this.onElementLoad);
  }

  /** @type {ViewerController['destroy']} */
  destroy() {
    this.unbindElement(this.el);
  }

  /** @type {ViewerController['enter']} */
  enter() {
    return this.viewerLifeCycle.enter(() => {
      this.emitter.emit('playing');
    });
  }

  /** @type {ViewerController['leave']} */
  leave() {
    return this.viewerLifeCycle.leave(() => {});
  }

  /** @type {ViewerController['off']} */
  off(...args) {
    this.emitter.off(...args);
  }

  /** @type {ViewerController['on']} */
  on(...args) {
    this.emitter.on(...args);
  }

  /**
   * Reakce na chybu v elementu.
   * @param {ErrorEvent} e
   */
  onElementError(e) {
    clearTimeout(this.loadTimeout);
    const error = new Error('Unknown HTMLIframeElement error.');
    this.emitter.emit('error', error);
  }

  /**
   * Reakce na načtení zdroje v elementu.
   * @param {Event} e
   */
  onElementLoad(e) {
    clearTimeout(this.loadTimeout);
    this.emitter.emit('loaded');
    const el = /** @type {IElement} */ (e.target);
    attachKeyListenerToIframe(el);
  }

  /**
   * Reakce na vypršení časovače pro load.
   */
  onLoadTimeout() {
    const error = new Error('HTMLIframeElement load timed out.');
    this.emitter.emit('error', error);
  }

  /** @type {ViewerController['release']} */
  release() {
    return this.viewerLifeCycle.release(() => {
      this.unbindElement(this.el);
      // Časovač pro načtení.
      clearTimeout(this.loadTimeout);
      // Zrušení hodnot v instanci.
      this.scheduleInfo = null;
    });
  }

  /** @type {ViewerController['seize']} */
  seize(scheduleInfo) {
    return this.viewerLifeCycle.seize(() => {
      // Nastavení hodnot v instanci.
      this.scheduleInfo = scheduleInfo;
      // Určení všech potřebných hodnot.
      return scheduleInfo.getUrl().then((url) => {
        const { el } = this;
        // Příprava zdrojového elementu.
        this.bindElement(el);
        // Nastavení oprávnění pro přístup k vybraným funkcionalitám.
        el.allow = 'autoplay;camera;geolocation;microphone';
        el.classList.add('viewer-controller-iframe');
        el.sandbox = 'allow-forms allow-same-origin allow-scripts';
        // Deaktivace scrollování v iframe.
        if (!this.scroll) {
          el.classList.add('viewer-controller-iframe--no-scroll');
          // Označeno jako deprecated, ale některé prohlížeče ho vyžadují.
          el.scrolling = 'no';
        }
        // Časovač pro načtení.
        this.loadTimeout = setTimeout(this.onLoadTimeout, this.loadDelay);
        // Nastavení zdroje.
        el.src = url;
      });
    });
  }

  /**
   * Uvolní naslouchání událostem z elementu.
   * @param {HTMLIFrameElement} el Element, kterému se naslouchá.
   * @private
   */
  unbindElement(el) {
    el.removeEventListener('error', this.onElementError);
    el.removeEventListener('load', this.onElementLoad);
    detachKeyListenerFromIframe(el);
  }
}

/**
 * @param {IElement} el
 */
function attachKeyListenerToIframe(el) {
  withIframeDocument(el, (iframeDoc) =>
    iframeDoc.addEventListener('keydown', onKeydown)
  );
}

/**
 * Vytvoří klon události klávesnice.
 * @param {KeyboardEvent} e
 * @returns {KeyboardEvent}
 */
function cloneKeyboardEvent(e) {
  return new KeyboardEvent(e.type, {
    bubbles: e.bubbles,
    cancelable: e.cancelable,
    key: e.key,
    keyCode: e.keyCode,
    code: e.code,
    location: e.location,
    repeat: e.repeat,
    ctrlKey: e.ctrlKey,
    shiftKey: e.shiftKey,
    altKey: e.altKey,
    metaKey: e.metaKey
  });
}

/**
 * @param {IElement} el
 */
function detachKeyListenerFromIframe(el) {
  withIframeDocument(el, (iframeDoc) =>
    iframeDoc?.removeEventListener('keydown', onKeydown)
  );
}

/**
 * @param {KeyboardEvent} e
 */
function onKeydown(e) {
  document.dispatchEvent(cloneKeyboardEvent(e));
}

/**
 * @param {IElement} el
 * @param {(doc: Document) => void} action
 */
function withIframeDocument(el, action) {
  try {
    const iframeDocument = el.contentDocument || el.contentWindow?.document;
    if (iframeDocument) {
      action(iframeDocument);
    }
  } catch {
    // Ignorování chyby kvůli prohlížečům, kde není dovoleno přistupovat k contentWindow na jiné originu.
  }
}
